import { Box, keyframes, useTheme } from '@mui/material';
import { PointMaterial, Points } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';

import avatarImageUrl from '@images/avatar-background.png';

import { spherePoints } from '../constants';
import { useSpherePoints } from '../hooks/useSpherePoints';

interface SpherePointsProps {
    isListening: boolean;
    analyserRef: React.RefObject<AnalyserNode | null>;
}

const SpherePoints = ({ isListening, analyserRef }: SpherePointsProps) => {
    const theme = useTheme();
    const { firstSphereRef, secondSphereRef, positions } = useSpherePoints(
        isListening,
        analyserRef
    );
    return (
        <>
            <Points ref={firstSphereRef} positions={positions} stride={3}>
                <PointMaterial
                    transparent
                    color={theme.palette.customColors.orange}
                    size={spherePoints.firstSpherePointSize}
                    sizeAttenuation={true}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </Points>
            <Points ref={secondSphereRef} positions={positions} stride={3}>
                <PointMaterial
                    transparent
                    color={theme.palette.customColors.purple}
                    size={spherePoints.secondSpherePointSize}
                    sizeAttenuation={true}
                    depthWrite={false}
                    blending={THREE.AdditiveBlending}
                />
            </Points>
        </>
    );
};

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

interface AnimatedSphereProps {
    isListening: boolean;
    analyserRef: React.RefObject<AnalyserNode | null>;
}

const AnimatedSphere = ({ isListening, analyserRef }: AnimatedSphereProps) => (
    <Box
        sx={{
            width: '80%',
            aspectRatio: '1 / 1',
            position: 'relative',
        }}
    >
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                height: '80%',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${avatarImageUrl})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    animation: `${rotate} 40s linear infinite`,
                    transformOrigin: 'center',
                    maskImage:
                        'radial-gradient(circle at center, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 90%)',
                    WebkitMaskImage:
                        'radial-gradient(circle at center, rgba(0,0,0,1) 60%, rgba(0,0,0,0) 90%)',
                    maskSize: '100% 100%',
                    WebkitMaskSize: '100% 100%',
                }}
            />
        </Box>
        <Canvas camera={{ position: [0, 0, 2.9] }}>
            <SpherePoints isListening={isListening} analyserRef={analyserRef} />
        </Canvas>
    </Box>
);

export default AnimatedSphere;
